import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { showError, showSuccess } from "@/utils/toast";

interface TranslationFile {
  id: number;
  name: string;
  url?: string | null;
  stats: {
    total: number;
    waiting_for_translation: number;
    submitted: number;
    accepted: number;
  };
}

interface NewFileDialogProps {
  gameId: number;
  versionId: number;
  onFileAdded: (newFile: TranslationFile) => void;
}

export const NewFileDialog = ({ gameId, versionId, onFileAdded }: NewFileDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [fileName, setFileName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCsrfToken = (): string | null => {
    const tokenEl = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
    return tokenEl ? tokenEl.content : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fileName) {
      showError("Minden mező kitöltése kötelező!");
      return;
    }
    setIsSubmitting(true);

    const csrfToken = getCsrfToken();
    if (!csrfToken) {
      showError("Biztonsági hiba: A CSRF token hiányzik.");
      setIsSubmitting(false);
      return;
    }

    const body = JSON.stringify({
      translation_file: { name: fileName }
    });

    try {
      const response = await fetch(`/api/games/${gameId}/versions/${versionId}/translation_files`, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "A fájl létrehozása sikertelen volt.");
      }

      const newFile = await response.json();
      showSuccess(`'${fileName}' fájl sikeresen létrehozva!`);
      onFileAdded(newFile);
      setIsOpen(false);
      setFileName("");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Ismeretlen hiba történt.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button>Új fájl létrehozása</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Új fordítási fájl létrehozása</DialogTitle>
          <DialogDescription>
            Adj meg egy nevet az új fordítási fájlnak.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="file-name" className="text-right">
                Név
              </Label>
              <Input
                id="file-name"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                className="col-span-3"
                placeholder="Pl. Alap játék szövegei"
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Mégsem
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !fileName}
            >
              {isSubmitting ? "Létrehozás..." : "Létrehozás"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};